import { Button, RotateCcw, cn } from "@app/ui";

import { useI18n } from "../../lib/useI18n.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { boardTargetKey, usePaletteBoardStore, type BoardTargetRef } from "../../stores/index.js";

/** Shared "arm the palette board" logic behind every color swatch — the
 *  labeled `EntityColorControl` below and the bigger bare swatch button in
 *  `EntityRow` both ultimately click through this. */
export function useArmColorTarget(targetRef: BoardTargetRef, boardAnchorId = "organize-board") {
  const mobile = useMobileUx();
  const armedTarget = usePaletteBoardStore((s) => s.target);
  const arm = usePaletteBoardStore((s) => s.arm);
  const armed = armedTarget !== null && boardTargetKey(armedTarget) === boardTargetKey(targetRef);

  function handleArm() {
    arm(targetRef);
    // The board sits above the list; on mobile it may be scrolled away — bring
    // it back so the user sees where the next tap lands.
    if (mobile) {
      document.getElementById(`setting-${boardAnchorId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return { armed, handleArm };
}

/** The one way to edit an entity color: a swatch that ARMS the palette board
 *  (the page-wide picking surface) instead of opening a native picker. Shows
 *  whether the color is an explicit override or the automatic palette default,
 *  with a revert affordance for overrides. */
export function EntityColorControl({
  targetRef,
  value,
  overridden,
  onRevert,
  boardAnchorId = "organize-board",
}: {
  targetRef: BoardTargetRef;
  /** The currently resolved hex (override or auto default). */
  value: string;
  overridden: boolean;
  /** Clear the override (back to auto); omit when there is no auto fallback. */
  onRevert?: () => void;
  /** SettingsAnchor id of the board card to reveal on mobile after arming. */
  boardAnchorId?: string;
}) {
  const { t } = useI18n();
  const { armed, handleArm } = useArmColorTarget(targetRef, boardAnchorId);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleArm}
        aria-pressed={armed}
        title={t("organize.colorTap")}
        className={cn(
          "flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1 text-sm hover:bg-accent",
          armed && "ring-2 ring-primary",
        )}
      >
        <span className="h-4 w-4 shrink-0 rounded-full border border-border" style={{ background: value }} aria-hidden />
        <span className="text-xs text-muted-foreground">
          {armed ? t("organize.colorArmed") : overridden ? t("organize.colorCustom") : t("organize.colorAuto")}
        </span>
      </button>
      {overridden && onRevert && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onRevert}
          title={t("organize.colorRevert")}
          aria-label={t("organize.colorRevert")}
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
